import { AgmCoreModule, MapsAPILoader } from "@agm/core";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import {
  BrowserAnimationsModule
} from "@angular/platform-browser/animations";
import { SharedModule } from "@common/_common.module";
import { AuthInterceptor } from "@hub/common/AuthInterceptor";
import { ConfirmationDialogComponent } from "@hub/components/v1/confirmation-dialog/confirmation-dialog.component";
import { LayoutComponent } from "@hub/components/v1/layout.component";
import { GlobalErrorHandler } from "@hub/core/utitilities/GlobalErrorHandler";
import { AppConfigService } from "@hub/services/AppConfigService";
// Security
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalModule, MsalRedirectComponent, MsalService } from "@azure/msal-angular";
import { NgSelectModule } from "@ng-select/ng-select";
import { AgGridModule } from "ag-grid-angular";
import { DragulaModule } from 'ng2-dragula';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MenubarModule } from 'primeng/menubar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PasswordModule } from 'primeng/password';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { EditorModule } from 'primeng/editor';
import { FieldsetModule } from 'primeng/fieldset';
import { GMapModule } from 'primeng/gmap';
import { InplaceModule } from 'primeng/inplace';
import { InputMaskModule } from 'primeng/inputmask';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MenuModule } from 'primeng/menu';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SidebarModule } from 'primeng/sidebar';
import { TabMenuModule } from 'primeng/tabmenu';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';
import { AppRoutingModule } from "./app-routing.module";
// Application Components
import { UrlSerializer } from "@angular/router";
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { ApiSettings } from "./_generated/apiSettings";
import {
  ClientController,
  ClientFinancialController,
  ClientGeneralController,
  ContactCardController,
  FilesController,
  GridController,
  LookupController,
  PublicController,
  ReferenceController,
  SearchController,
  SettingsController,
  TVPackagesController,
  TestController,
  Tv10Controller,
  UserController
} from "./_generated/services";
import { MsalCustomInterceptor } from "./common/MsalCustomInterceptor";
import { AppComponent } from "./components/app.component";
import { MainRedirectComponent, SyncBookingComponent } from "./components/v1/_common";
import { UnauthorizedComponent } from "./components/v1/authetication";
import { FooterComponent } from "./components/v1/footer/footer.component";
import { HeaderComponent } from "./components/v1/header/header.component";
import { MenuComponent } from "./components/v1/header/menu/menu.component";
import { FormV2Module } from "./components/v2/form-module/form-module";
import { AGCheckBoxEditorComponent } from "./components/v2/table/grid-components/g-checkbox/g-checkbox-editor.component";
import { AGCheckBoxRendererComponent } from "./components/v2/table/grid-components/g-checkbox/g-checkbox-renderer.component";
import { AGCurrencyRendererComponent } from "./components/v2/table/grid-components/g-currency/g-currency.component";
import { AGDatepickerEditorComponent } from "./components/v2/table/grid-components/g-datepicker/g-datepicker.component";
import { AGDeleteButtonComponent } from "./components/v2/table/grid-components/g-delete-button/g-delete-button.component";
import { AGDropdownEditorComponent } from "./components/v2/table/grid-components/g-dropdown/g-dropdown.component";
import { AGHyperlinkComponent } from "./components/v2/table/grid-components/g-hyperlink/g-hyperlink.component";
import { AGInputEditorComponent } from "./components/v2/table/grid-components/g-input/g-input.component";
import { AGTextAreaEditorComponent } from "./components/v2/table/grid-components/g-textarea/g-textarea.component";
import { MainGridContentComponent } from "./components/v2/table/main-grid-content.component";
import { MainGridDialogComponent } from "./components/v2/table/main-grid-dialog.component";
import { MainGridV2Component } from "./components/v2/table/main-grid.component";
import { SubgridV2Component } from "./components/v2/table/subgrid.component";
import { MainGridOptionWrapperComponent } from "./components/v2/table/wrappers/main-grid-options-wrapper.component";
import { LowerCaseUrlSerializer } from "./core/utitilities/UrlSerializer";
import { CustomLazyGoogleApiKeyLoader } from "./services/CustomLazyGoogleApiKeyLoader";

const appInitializerFn = (appConfig: AppConfigService) => {
  return () => {
    return appConfig.loadPublicAppConfig();
  };
};

const GENERATED_API_CONTROLLERS = [
  SearchController,
  TestController,
  ReferenceController,
  ClientController,
  FilesController,
  GridController,
  ClientFinancialController,
  ClientGeneralController,
  PublicController,
  LookupController,
  TVPackagesController,
  SettingsController,
  ContactCardController,
  UserController,
  Tv10Controller
];

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (logLevel === LogLevel.Error) {
    console.error(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: ApiSettings.AzureClientId,
			authority: ApiSettings.AzureAuthority,
			redirectUri: pathjoin(window.location.origin, "login"),
			postLogoutRedirectUri: window.location.origin,
			navigateToLoginRequestUrl: true
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: true,
		},
		system: {
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Error,
				piiLoggingEnabled: false
			}
		}
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
	protectedResourceMap.set(ApiSettings.ApiUrl, [ApiSettings.AzureAudience + "/user_impersonation"]);

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: ['user.read']
		},
		loginFailedRoute: '/unauthorized'
	};
}

@NgModule({
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    CommonModule,
    AgGridModule,
    AgmCoreModule,
    PanelMenuModule,
    MenubarModule,
    ButtonModule,
    BreadcrumbModule,
    InputTextModule,
    InputTextareaModule,
    PasswordModule,
    DialogModule,
    DropdownModule,
    FileUploadModule,
    ConfirmDialogModule,
    PaginatorModule,
    MsalModule,
    TabMenuModule,
    CheckboxModule,
    TooltipModule,
    InputMaskModule,
    PanelModule,
    FieldsetModule,
    RadioButtonModule,
    OverlayPanelModule,
    InplaceModule,
    AutoCompleteModule,
    ToggleButtonModule,
    MultiSelectModule,
    CalendarModule,
    InputSwitchModule,
    SelectButtonModule,
    EditorModule,
    MessagesModule,
    TabViewModule,
    MenuModule,
    GMapModule,
    SidebarModule,
    ToastModule,
    // Appling serve tion Modules
    SharedModule,
    FormV2Module,
    DragulaModule.forRoot()
  ],
  declarations: [
    AppComponent,
    FooterComponent,
    UnauthorizedComponent,
    LayoutComponent,
    MenuComponent,
    HeaderComponent,
    MainRedirectComponent,
    SyncBookingComponent,
    ConfirmationDialogComponent,
    AGInputEditorComponent,
    AGDropdownEditorComponent,
    AGHyperlinkComponent,
    AGCurrencyRendererComponent,
    MainGridContentComponent,
    MainGridDialogComponent,
    MainGridV2Component,
    SubgridV2Component,
    AGCheckBoxEditorComponent,
    AGCheckBoxRendererComponent,
    AGDatepickerEditorComponent,
    AGDeleteButtonComponent,
    AGTextAreaEditorComponent,
    MainGridOptionWrapperComponent
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [AppConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },    
		MsalService,
		MsalGuard,
		MsalBroadcastService,
    // Services
    ConfirmationService,
    MessageService,
    PublicController,
    SettingsController,

    // Guards
    {
      provide: MapsAPILoader,
      useClass: CustomLazyGoogleApiKeyLoader
    },
		{
			provide: UrlSerializer,
			useClass: LowerCaseUrlSerializer,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalCustomInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true,
		},
    ...GENERATED_API_CONTROLLERS,

		// Error Handling, this has to be last
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler,
		}
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})

export class AppModule {
}

function pathjoin(base: string, path: string): string {
  var sanitizedBase = base.trim();
  if (sanitizedBase[sanitizedBase.length] == "/")
    sanitizedBase = sanitizedBase.slice(0, -1)

  var sanitizedPath = path.trim();
  if (sanitizedPath[0] == "/")
    sanitizedPath = sanitizedPath.substring(1);

  return `${sanitizedBase}/${sanitizedPath}`;
}

